import type {
  ActivityFeedEntry,
  ActivitySourceDetailResponse,
  TransactionDetailResponse
} from "@salesko/contracts";
import { ExternalLink, FileSearch, History, Loader2, Network, ReceiptText, X } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { useI18n } from "../../i18n";
import {
  activityEntryTypeLabel,
  activityEvidenceStateLabel,
  activitySubjectTypeLabel,
  formatMinorUnitAmount,
  isAvailableActivitySource,
  transactionKindLabel,
  transactionStatusLabel
} from "../lib/activityFeed";
import { compactDateTime } from "../lib/format";

export type ActivitySourceSelection = {
  entryId: string;
  sourceReferenceHash: string;
};

export type ActivityFeedPanelProps = {
  enabled: boolean;
  items: ActivityFeedEntry[];
  pending: boolean;
  initialError?: string | undefined;
  hasNext: boolean;
  loadingMore: boolean;
  loadMoreError?: string | undefined;
  selectedTransactionId: string | null;
  transactionDetail: TransactionDetailResponse | null;
  transactionDetailPending: boolean;
  transactionDetailError?: string | undefined;
  selectedSource: ActivitySourceSelection | null;
  sourceDetail: ActivitySourceDetailResponse | null;
  sourceDetailPending: boolean;
  sourceDetailError?: string | undefined;
  onSelectTransaction: (canonicalTransactionId: string) => void;
  onOpenTransactionGraph: (canonicalTransactionId: string) => void;
  onCloseTransactionDetail: () => void;
  onSelectSource: (entryId: string, sourceReferenceHash: string) => void;
  onCloseSourceDetail: () => void;
  onOpenSourceFrame: (frameRecordId: string, nodeId?: string) => void;
  onLoadMore: () => void;
};

export function ActivityFeedPanel({
  enabled,
  hasNext,
  initialError,
  items,
  loadMoreError,
  loadingMore,
  onCloseSourceDetail,
  onCloseTransactionDetail,
  onLoadMore,
  onOpenSourceFrame,
  onOpenTransactionGraph,
  onSelectSource,
  onSelectTransaction,
  pending,
  selectedSource,
  selectedTransactionId,
  sourceDetail,
  sourceDetailError,
  sourceDetailPending,
  transactionDetail,
  transactionDetailError,
  transactionDetailPending
}: ActivityFeedPanelProps) {
  const { locale, t } = useI18n();
  const sourceDetailId = useId();
  const transactionDetailId = useId();
  const sourceDetailRef = useRef<HTMLElement>(null);
  const transactionDetailRef = useRef<HTMLElement>(null);
  const sourceTriggerRef = useRef<HTMLButtonElement | null>(null);
  const transactionTriggerRef = useRef<HTMLButtonElement | null>(null);

  const matchingSourceDetail =
    selectedSource &&
    sourceDetail?.entryId === selectedSource.entryId &&
    sourceDetail.sourceReferenceHash === selectedSource.sourceReferenceHash
      ? sourceDetail
      : null;
  const matchingTransactionDetail =
    selectedTransactionId && transactionDetail?.canonicalTransactionId === selectedTransactionId
      ? transactionDetail
      : null;

  useEffect(() => {
    if (selectedSource) {
      sourceDetailRef.current?.focus();
    }
  }, [selectedSource]);

  useEffect(() => {
    if (selectedTransactionId) {
      transactionDetailRef.current?.focus();
    }
  }, [selectedTransactionId]);

  const closeSourceDetail = () => {
    const trigger = sourceTriggerRef.current;
    onCloseSourceDetail();
    restoreFocus(trigger);
  };
  const closeTransactionDetail = () => {
    const trigger = transactionTriggerRef.current;
    onCloseTransactionDetail();
    restoreFocus(trigger);
  };

  return (
    <div className="activity-feed-panel">
      <header className="activity-feed-header">
        <span aria-hidden="true" className="activity-feed-mark">
          <History size={20} />
        </span>
        <div>
          <p className="eyebrow">{t.activityFeed.eyebrow}</p>
          <h2>{t.activityFeed.title}</h2>
          <span aria-live="polite">
            {enabled ? t.activityFeed.pageSummary(items.length, hasNext) : t.activityFeed.unavailable}
          </span>
        </div>
      </header>

      {!enabled ? (
        <p className="activity-feed-empty">{t.activityFeed.unavailable}</p>
      ) : pending ? (
        <div aria-live="polite" className="activity-feed-loading" role="status">
          <Loader2 aria-hidden="true" className="spin" size={18} />
          <span>{t.activityFeed.loading}</span>
        </div>
      ) : initialError ? (
        <p className="activity-feed-error" role="alert">{initialError}</p>
      ) : items.length === 0 ? (
        <p className="activity-feed-empty">{t.activityFeed.empty}</p>
      ) : (
        <div aria-label={t.activityFeed.title} className="activity-feed-list" role="feed">
          {items.map((item) => {
            const availableSources = item.sources.filter(isAvailableActivitySource);
            const transactionSelected = selectedTransactionId === item.subjectId;

            return (
              <article aria-busy="false" className="activity-feed-card" key={item.id} tabIndex={0}>
                <div className="activity-feed-card-top">
                  <div className="activity-feed-badges">
                    <span className={`activity-feed-type activity-feed-type-${item.subjectType}`}>
                      {activityEntryTypeLabel(item.entryType, t.activityFeed)}
                    </span>
                    <span className="activity-feed-subject">
                      {activitySubjectTypeLabel(item.subjectType, t.activityFeed)}
                    </span>
                    {item.backfilled ? <span className="activity-feed-history">{t.activityFeed.backfilled}</span> : null}
                    {item.entryType === "transaction.corrected" ? (
                      <span className="activity-feed-correction">{t.activityFeed.correction}</span>
                    ) : null}
                  </div>
                </div>

                <h3>{item.title}</h3>
                <p>{item.summary}</p>

                <dl className="activity-feed-times">
                  <div>
                    <dt>{t.activityFeed.recordedAt}</dt>
                    <dd><time dateTime={item.recordedAt}>{compactDateTime(item.recordedAt)}</time></dd>
                  </div>
                  <div>
                    <dt>{t.activityFeed.occurredAt}</dt>
                    <dd>
                      {item.occurredAt ? (
                        <time dateTime={item.occurredAt}>{compactDateTime(item.occurredAt)}</time>
                      ) : t.activityFeed.timeUnavailable}
                    </dd>
                  </div>
                  <div>
                    <dt>{t.activityFeed.evidence}</dt>
                    <dd>{activityEvidenceStateLabel(item.evidenceState, t.activityFeed)}</dd>
                  </div>
                </dl>

                {item.supersedesEntryId ? (
                  <p className="activity-feed-supersedes">{t.activityFeed.supersedes}</p>
                ) : null}

                <div className="activity-feed-actions">
                  {availableSources.map((source, index) => {
                    const sourceSelected =
                      selectedSource?.entryId === item.id &&
                      selectedSource.sourceReferenceHash === source.sourceReferenceHash;
                    return (
                      <button
                        aria-controls={sourceDetailId}
                        aria-expanded={sourceSelected}
                        aria-label={t.activityFeed.inspectSourceFor(item.title, index + 1)}
                        className="secondary-button"
                        key={source.sourceReferenceHash}
                        onClick={(event) => {
                          sourceTriggerRef.current = event.currentTarget;
                          onSelectSource(item.id, source.sourceReferenceHash);
                        }}
                        type="button"
                      >
                        <FileSearch aria-hidden="true" size={15} />
                        <span>{t.activityFeed.inspectSource(index + 1)}</span>
                      </button>
                    );
                  })}
                  {item.subjectType === "transaction" ? (
                    <>
                      <button
                        aria-controls={transactionDetailId}
                        aria-expanded={transactionSelected}
                        aria-label={t.activityFeed.inspectTransactionFor(item.title)}
                        className="secondary-button"
                        onClick={(event) => {
                          transactionTriggerRef.current = event.currentTarget;
                          onSelectTransaction(item.subjectId);
                        }}
                        type="button"
                      >
                        <ReceiptText aria-hidden="true" size={15} />
                        <span>{t.activityFeed.inspectTransaction}</span>
                      </button>
                      <button
                        aria-label={t.activityFeed.openTransactionGraphFor(item.title)}
                        className="secondary-button"
                        data-transaction-graph-id={item.subjectId}
                        onClick={() => onOpenTransactionGraph(item.subjectId)}
                        type="button"
                      >
                        <Network aria-hidden="true" size={15} />
                        <span>{t.activityFeed.openTransactionGraph}</span>
                      </button>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {enabled && !pending && !initialError && items.length > 0 ? (
        <div className="activity-feed-pagination">
          {hasNext ? (
            <button className="secondary-button" disabled={loadingMore} onClick={onLoadMore} type="button">
              {loadingMore ? <Loader2 aria-hidden="true" className="spin" size={16} /> : null}
              <span>{loadingMore ? t.activityFeed.loadingOlder : t.activityFeed.loadOlder}</span>
            </button>
          ) : null}
          {loadMoreError ? <p className="activity-feed-error" role="alert">{loadMoreError}</p> : null}
        </div>
      ) : null}

      {selectedSource ? (
        <aside
          aria-labelledby={`${sourceDetailId}-title`}
          className="activity-feed-detail"
          id={sourceDetailId}
          ref={sourceDetailRef}
          role="region"
          tabIndex={-1}
        >
          <div className="activity-feed-detail-header">
            <h3 id={`${sourceDetailId}-title`}>{t.activityFeed.sourceDetailTitle}</h3>
            <button
              aria-label={t.activityFeed.closeSourceDetail}
              className="icon-button"
              onClick={closeSourceDetail}
              title={t.activityFeed.closeSourceDetail}
              type="button"
            >
              <X aria-hidden="true" size={17} />
            </button>
          </div>
          {sourceDetailError ? (
            <p className="activity-feed-error" role="alert">{sourceDetailError}</p>
          ) : sourceDetailPending || !matchingSourceDetail ? (
            <div aria-live="polite" className="activity-feed-loading" role="status">
              <Loader2 aria-hidden="true" className="spin" size={18} />
              <span>{t.activityFeed.loadingSource}</span>
            </div>
          ) : (
            <>
              <section className="activity-feed-quote">
                <h4>{t.activityFeed.exactQuote}</h4>
                <blockquote>{matchingSourceDetail.quote}</blockquote>
              </section>
              <button
                className="secondary-button"
                onClick={() => onOpenSourceFrame(matchingSourceDetail.frameRecord.id, matchingSourceDetail.nodeId)}
                type="button"
              >
                <ExternalLink aria-hidden="true" size={15} />
                <span>{t.activityFeed.openSourceFrame}</span>
              </button>
            </>
          )}
        </aside>
      ) : null}

      {selectedTransactionId ? (
        <aside
          aria-labelledby={`${transactionDetailId}-title`}
          className="activity-feed-detail"
          id={transactionDetailId}
          ref={transactionDetailRef}
          role="region"
          tabIndex={-1}
        >
          <div className="activity-feed-detail-header">
            <h3 id={`${transactionDetailId}-title`}>{t.activityFeed.transactionDetailTitle}</h3>
            <button
              aria-label={t.activityFeed.closeTransactionDetail}
              className="icon-button"
              onClick={closeTransactionDetail}
              title={t.activityFeed.closeTransactionDetail}
              type="button"
            >
              <X aria-hidden="true" size={17} />
            </button>
          </div>
          {transactionDetailError ? (
            <p className="activity-feed-error" role="alert">{transactionDetailError}</p>
          ) : transactionDetailPending || !matchingTransactionDetail ? (
            <div aria-live="polite" className="activity-feed-loading" role="status">
              <Loader2 aria-hidden="true" className="spin" size={18} />
              <span>{t.activityFeed.loadingTransaction}</span>
            </div>
          ) : (
            <TransactionDetail detail={matchingTransactionDetail} locale={locale} />
          )}
        </aside>
      ) : null}
    </div>
  );
}

function TransactionDetail({ detail, locale }: { detail: TransactionDetailResponse; locale: string }) {
  const { t } = useI18n();
  const { data, sourceFact } = detail.transaction;

  return (
    <div className="activity-transaction-detail">
      <div>
        <h4>{detail.transaction.label}</h4>
        {detail.transaction.summary ? <p>{detail.transaction.summary}</p> : null}
      </div>
      <dl className="activity-feed-detail-list activity-transaction-fields">
        <div>
          <dt>{t.activityFeed.kind}</dt>
          <dd>{transactionKindLabel(data.kind, t.activityFeed)}</dd>
        </div>
        <div>
          <dt>{t.activityFeed.externalId}</dt>
          <dd>{data.externalId}</dd>
        </div>
        <div>
          <dt>{t.activityFeed.status}</dt>
          <dd>{transactionStatusLabel(data.status, t.activityFeed)}</dd>
        </div>
        <div>
          <dt>{t.activityFeed.amount}</dt>
          <dd>{formatMinorUnitAmount(data.amountMinor, data.currency, locale)}</dd>
        </div>
        <div>
          <dt>{t.activityFeed.occurredAt}</dt>
          <dd><time dateTime={detail.transaction.occurredAt}>{compactDateTime(detail.transaction.occurredAt)}</time></dd>
        </div>
        <div>
          <dt>{t.activityFeed.parties}</dt>
          <dd>{data.partyCanonicalEntityIds.length}</dd>
        </div>
      </dl>
      <section className="activity-transaction-provenance">
        <h4>{t.activityFeed.provenance}</h4>
        <dl className="activity-feed-detail-list">
          <div>
            <dt>{t.activityFeed.acceptedAt}</dt>
            <dd><time dateTime={detail.acceptedAt}>{compactDateTime(detail.acceptedAt)}</time></dd>
          </div>
          <div>
            <dt>{t.activityFeed.backfilledLabel}</dt>
            <dd>{t.activityFeed.backfilledValue(sourceFact.backfilled)}</dd>
          </div>
          <div>
            <dt>{t.activityFeed.sourceFrames}</dt>
            <dd>{sourceFact.sourceFrameRecordIds.length}</dd>
          </div>
          <div>
            <dt>{t.activityFeed.evidenceSpans}</dt>
            <dd>{sourceFact.evidenceSpanIds.length}</dd>
          </div>
          <div>
            <dt>{t.activityFeed.sourceReferences}</dt>
            <dd>{sourceFact.sourceReferenceHashes.length}</dd>
          </div>
        </dl>
        <p className="activity-feed-evidence"><strong>{t.activityFeed.evidenceText}</strong> {detail.transaction.evidence}</p>
      </section>
    </div>
  );
}

function restoreFocus(target: HTMLButtonElement | null): void {
  if (!target) {
    return;
  }
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => target.focus());
    return;
  }
  target.focus();
}
